import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, interval, tap } from 'rxjs';
import { LibraryConfigurationService } from '../../core/library-configuration.service';
import { LoggerService } from '../../core/logger.service';
import { UIMesageService } from '../../core/ui-message.service';
import { EssDialogService } from '../dialogs/dialog-service';
import { EssOkCancelDialogComponent } from '../dialogs/ok-cancel-dialog.component';

@Injectable({
    providedIn: 'root',
})
export class EssSoftwareUpdateService {
    public promptUser = false;
    private reloadRequested = false;
    constructor(
        private logger: LoggerService,
        private dialogService: EssDialogService,
        private swUpdates: SwUpdate,
        private libraryConfiguration: LibraryConfigurationService,
        private uiMessage: UIMesageService,
    ) {
        this.logger.log('EssSoftwareUpdateService: constructor');
        this.subscribeVersionUpdates();
    }

    public setTimeTrigger(secondsToCheck?: number) {
        this.logger.log('timeTrigger');
        if (!secondsToCheck) {
            if (this.libraryConfiguration.isDebug) {
                secondsToCheck = 1;
            } else {
                secondsToCheck = 60;
            }
        }
        // polling for updates with `interval()`.
        const timeTrigger$ = interval(secondsToCheck * 1000);
        /*

        // Allow the app to stabilize first, before starting
        // ?? appRef.isStable. does not work for scrumpoker
        const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));//
        const timeTriggerOnceAppIsStable$ = concat(appIsStable$, timeTrigger$);
        timeTriggerOnceAppIsStable$.subscribe(async () => {
        */
        timeTrigger$.subscribe(async () => {
            this.logger.log('timeTrigger');
            try {
                this.logger.log('checkForUpdate');
                if (!this.reloadRequested) {
                    this.logger.log('!reloadRequested checkForUpdate');
                    const updateFound = await this.swUpdates.checkForUpdate();
                    if (updateFound) {
                        this.logger.warn('Update found');
                    } else {
                        this.logger.log('On the latest version.');

                    }
                    // the relaod is requested indirectly by subscribeForAutomaticUpdate
                }
            } catch (err) {
                this.logger.log('Failed to check for updates:', err);
            }
        });
    }

    /**
     * Reload that first activates a waiting service-worker update, so a client on an old build reaches the new one
     * with a single reload (whocan ConnectionStalledBanner, workorder app-check-connection-stalled-hint-2026-10-26).
     * `checkForUpdate` + `activateUpdate` race against `timeoutMs`; every error is ignored and the page reloads
     * anyway. Without an active service worker (`isEnabled === false`, e.g. locally) it reloads directly.
     */
    public async reloadWithSwUpdate(timeoutMs = 3000): Promise<void> {
        if (this.swUpdates.isEnabled) {
            const update = (async () => {
                await this.swUpdates.checkForUpdate();
                await this.swUpdates.activateUpdate();
            })();
            const timeout = new Promise<void>(resolve => setTimeout(resolve, timeoutMs));
            try {
                await Promise.race([update, timeout]);
            } catch (error) {
                this.logger.log('reloadWithSwUpdate: update step failed, reloading anyway', error);
            }
        }
        document.location.reload();
    }

    private subscribeVersionUpdates() {
        this.swUpdates.versionUpdates.subscribe((evt) => {
            switch (evt.type) {
                case 'VERSION_DETECTED':
                    this.logger.warn(`Downloading new app version, hash: ${evt.version.hash}`);
                    break;
                case 'VERSION_READY':
                    this.logger.warn(`Current app version: ${evt.currentVersion.hash}`);
                    this.logger.warn(`New app version ready for use, hash: ${evt.latestVersion.hash}`);
                    this.requestReload();
                    break;
                case 'VERSION_INSTALLATION_FAILED':
                    this.logger.error(`Failed to install app version, hash: '${evt.version.hash}': ${evt.error}`);
                    break;
                case 'NO_NEW_VERSION_DETECTED':
                    this.logger.log('Current version hash', evt.version.hash);
                    break;
                default:
                    this.logger.log('Software update event', evt);
            }
        });
    }

    private requestReload() {
        this.logger.log('requestReload');
        let secondsToWait: number
        if (this.libraryConfiguration.isDebug) {
            secondsToWait = 1;
        } else {
            secondsToWait = 20;
        }
        if (!this.reloadRequested) {
            this.reloadRequested = true;
            if (this.promptUser) {
                const dialogRef = this.dialogService.open(
                    EssOkCancelDialogComponent,
                    {
                        title: 'Update',
                        description1:
                            'A new ' +
                            this.libraryConfiguration.appName +
                            ' version is available.',
                        description2:
                            'To continue to use all the features please refresh this browser window.',
                        okLabel: 'Refresh',
                        cancelLabel: 'Cancel',
                    }
                );
                dialogRef.afterClosed().subscribe(async (result) => {
                    this.logger.log('The dialog was closed');
                    if (result) {
                        this.logger.log('Refresh', result);
                        document.location.reload();
                        this.reloadRequested = false;
                    } else {
                        this.reloadRequested = false;
                        this.logger.log('Canceled');
                    }
                });
            } else {
                this.uiMessage.warning('A new ' +
                    this.libraryConfiguration.appName +
                    ' version is available. '
                    + this.libraryConfiguration.appName + ' will reload in few seconds.')
                setTimeout(() => {
                    document.location.reload();
                    this.reloadRequested = false;
                }, 1000 * secondsToWait);

            }
        } else {
            this.logger.warn('Reload was requested already');
        }
    }
}
